import { useEffect, useRef, useState } from "react";

// Ambient filler lines — used ONLY in gaps between real backend checkpoints so the terminal
// never stalls visually during the long LLM call. They're styled differently from real events
// in the UI (dimmer, no green dot) so the user knows what's authoritative.
const AMBIENT_STAGES = [
  ["scanning declassified government corpus...", "filtering by topic relevance + recency", "tagging primary-source candidates"],
  ["cross-referencing FOIA reading rooms", "matching court filings // PACER + state", "pulling congressional hearing transcripts"],
  ["tracing financial intermediaries", "mapping shell entities + jurisdictions", "flagging contradicted mainstream summaries"],
  ["compiling chronological timeline", "deduplicating actor roster", "scoring credibility tier"],
  ["drafting Executive Summary", "drafting Key Claims + Evidence", "drafting Counter-Evidence + Open Threads"],
  ["extracting source URLs", "stripping fabricated references", "final pass — verifying citation density"],
  ["// LLM still composing — payload streaming", "// large dossier in progress — hold steady", "// background task running, no timeout"],
];

/**
 * useDossierProgress
 *  - `active`: boolean — when false, returns []
 *  - `realEvents`: list of `{ ts, label }` real backend checkpoints (from polling response)
 * Returns a unified list of `{ label, kind: "real"|"ambient" }` ordered by insertion time.
 * Real events are always shown in order they arrive (we dedupe by index, not by content).
 * Ambient lines are pushed in the background on a ~2.4s cadence ONLY when no new real event has
 * arrived in the previous 2s, so we don't spam ambient noise on top of real events.
 */
export function useDossierProgress(active, realEvents) {
  const [lines, setLines] = useState([]);
  const ambientIdx = useRef(0);             // next ambient slot to emit
  const realCursor = useRef(0);             // how many real events we've already merged
  const lastRealAtRef = useRef(0);          // wall-clock ms of the most-recent real event we appended
  const safeReal = Array.isArray(realEvents) ? realEvents : [];

  // Merge any NEW real events into the line stream as soon as they appear in the polling response.
  // Compute a `deltaMs` for each real event = ms since the previous real event's timestamp
  // (or 0 for the very first one). The UI uses this for the small "(+12.4s)" suffix.
  useEffect(() => {
    if (!active) return;
    if (safeReal.length > realCursor.current) {
      const fresh = [];
      for (let i = realCursor.current; i < safeReal.length; i++) {
        const cur = safeReal[i];
        const prev = i > 0 ? safeReal[i - 1] : null;
        let deltaMs = 0;
        try {
          if (prev?.ts && cur?.ts) {
            deltaMs = Math.max(0, new Date(cur.ts).getTime() - new Date(prev.ts).getTime());
          }
        } catch { /* malformed ts — leave deltaMs = 0 */ }
        fresh.push({ label: cur.label, kind: "real", deltaMs });
      }
      realCursor.current = safeReal.length;
      lastRealAtRef.current = Date.now();
      setLines((arr) => [...arr, ...fresh]);
    }
  }, [safeReal, active]);

  // Reset when activation flips off
  useEffect(() => {
    if (!active) {
      setLines([]);
      ambientIdx.current = 0;
      realCursor.current = 0;
      lastRealAtRef.current = 0;
    }
  }, [active]);

  // Ambient ticker — fires on a 2.4s cadence, but skips its turn if a real event arrived in the
  // last 2 seconds (avoids stepping on real progress with filler).
  useEffect(() => {
    if (!active) return undefined;
    const tick = () => {
      if (Date.now() - lastRealAtRef.current < 2000) return;
      setLines((arr) => {
        if (arr.length >= 28) return arr; // hard cap so the stream doesn't scroll forever
        const i = ambientIdx.current;
        const stage = AMBIENT_STAGES[Math.floor(i / 3) % AMBIENT_STAGES.length];
        const slot = i % 3;
        ambientIdx.current = i + 1;
        return [...arr, { label: stage[slot], kind: "ambient" }];
      });
    };
    const t1 = setTimeout(tick, 1800); // first ambient line shortly after a real event lands
    const t2 = setTimeout(tick, 3600);
    const interval = setInterval(tick, 2400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearInterval(interval);
    };
  }, [active]);

  return lines;
}
